import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '@mui/material';
import {
  Button,
  Checkbox,
  Icon,
  Modal,
  SelectMenu,
  TextArea,
  TextField,
  Typography,
} from '@scale/scaleui';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { ManuallyRemoveWorkers } from 'react-scale/components/ExpertManagement/ExpertManagementPage/CommunityManagement/ManuallyRemoveWorkers';
import { useFilteredPodTypeOptions } from 'react-scale/components/ExpertManagement/hooks/useFilteredPodTypeOptions';
import { Routes, buildRoute } from 'react-scale/components/ExpertManagement/urls';
import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { useFetchWorkersManagers } from '../../hooks/queries/useFetchWorkersManagers';
import { SelectWorkerManager } from '../SelectWorkerManager';
import { promoteToTopLevelManager, setManagerForWorkers } from '../api';

function ManuallyAssignWorkers() {
  const history = useHistory();
  const [workerIds, setWorkerIds] = useState<string>('');

  const [manager, setManager] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  const canUpload = useMemo(
    () => !isUploading && manager !== null && workerIds.length > 0,
    [manager, workerIds, isUploading],
  );

  const handleAssignmentSubmit = useCallback(async () => {
    if (!manager) {
      return;
    }
    setIsUploading(true);
    const workerIdsArray = workerIds.split(/[^a-zA-Z0-9]+/).filter(id => id.length > 0);
    try {
      const result = await setManagerForWorkers(manager, workerIdsArray);
      if (result.success) {
        history.push(buildRoute(Routes.ExpertManagement, { user: manager }));
      } else {
        defaultApiFetchErrorHandler({ name: 'error', message: result.error, logError: () => null });
      }
    } finally {
      setIsUploading(false);
    }
  }, [workerIds, manager, history]);

  return (
    <div className="flex flex-col gap-2">
      <Typography.Subtitle2>Manually Assign Squad Members</Typography.Subtitle2>
      <Typography.Paragraph2>
        Select a leader and add a list of worker IDs to add all workers as reports of the leader.
        Any workers assigned to a TLC will automatically become TLs, and any workers assigned to TLs
        will automatically become Contributors.
      </Typography.Paragraph2>

      <SelectWorkerManager
        placeholder="Select a leader to assign workers to"
        selectedManagerId={manager}
        setSelectedManagerId={setManager}
      />
      <TextArea
        id="worker-id-addition"
        value={workerIds}
        onChange={e => setWorkerIds(e.target.value)}
        style={{ height: 200 }}
      />
      <div>
        <Button disabled={!canUpload} onClick={() => setShowConfirmationModal(true)}>
          Update Worker/Leader
        </Button>
      </div>
      {showConfirmationModal && (
        <WorkerAssignmentConfirmationModal
          open={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          workerIds={workerIds}
          managerId={manager}
          onConfirm={handleAssignmentSubmit}
        />
      )}
    </div>
  );
}

function WorkerAssignmentConfirmationModal({
  open,
  onClose,
  workerIds,
  managerId,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  workerIds: string;
  managerId: string;
  onConfirm: () => void;
}) {
  const workerIdsArray = useMemo(
    () => workerIds.split(/[^a-zA-Z0-9]+/).filter(id => id.length > 0),
    [workerIds],
  );
  const { loading, workerManagers, managerEmail } = useFetchWorkersManagers(
    open,
    workerIdsArray,
    managerId,
  );

  return (
    <Modal
      title="Confirm Worker Assignment"
      open={open}
      onClose={onClose}
      actions={
        <div className="flex items-center gap-2 justify-end">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            Assign Workers ({workerIdsArray.length})
          </Button>
        </div>
      }
      theme="scale-theme"
    >
      <div className="flex flex-col gap-2 min-h-[240px]">
        <Typography.Subtitle3 className="mb-2">
          Are you sure you want to assign the following workers to {managerEmail}?
        </Typography.Subtitle3>
        <CurrentWorkerAssignmentTable
          workerIds={workerIdsArray}
          workerManagers={workerManagers}
          loading={loading}
        />
      </div>
    </Modal>
  );
}

export function CurrentWorkerAssignmentTable({
  workerIds,
  workerManagers,
  loading,
}: {
  workerIds: string[];
  workerManagers: Record<string, any>;
  loading: boolean;
}) {
  return (
    <>
      <div className="flex flex-nowrap items-center mb-2">
        <Typography.Subtitle3 className="w-64">Worker</Typography.Subtitle3>
        <Typography.Subtitle3 className="w-32">Role</Typography.Subtitle3>
        <Typography.Subtitle3 className="w-80">Current Reviewer</Typography.Subtitle3>
      </div>
      {workerIds.map(workerId => (
        <div
          key={workerId}
          className="flex flex-nowrap items-center mb-2 pb-2 border-b border-neutral-200"
        >
          <div>
            <Typography.Paragraph3 className="w-64 break-words">
              {workerManagers[workerId]?.email}
            </Typography.Paragraph3>
            <Typography.Paragraph3>{workerId}</Typography.Paragraph3>
          </div>
          <Typography.Paragraph2 className="w-32 break-words">
            {loading ? 'Loading' : workerManagers[workerId]?.role ?? 'N/A'}
          </Typography.Paragraph2>
          <Typography.Paragraph2 className="flex items-center w-80 break-words">
            {loading ? 'Loading' : workerManagers[workerId]?.managerEmail}
            {workerManagers[workerId]?.managerEmail?.includes('@') && (
              <Icon
                icon="exclamation-triangle"
                variant="warning"
                size="sm"
                tooltip="Please ensure that this manager is aware of this change"
                tooltipProps={{ side: 'left' }}
              />
            )}
          </Typography.Paragraph2>
        </div>
      ))}
    </>
  );
}

function PromoteManager() {
  const queryClient = useQueryClient();
  const history = useHistory();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [manager, setManager] = useState<{ label: string; value: string } | undefined>(undefined);
  const [podType, setPodType] = useState<string>('');
  const [showSlackDetails, setShowSlackDetails] = useState<boolean>(false);
  const [slackChannelName, setSlackChannelName] = useState<string>('');
  const [remoChecked, setRemoChecked] = useState<boolean>(false);
  const [expertChecked, setExpertChecked] = useState<boolean>(false);
  const filteredPodTypeOptions = useFilteredPodTypeOptions();

  const canUpload = useMemo(
    () =>
      !isUploading &&
      manager !== undefined &&
      podType !== '' &&
      (!showSlackDetails || (slackChannelName !== '' && (remoChecked || expertChecked))),
    [isUploading, manager, podType, showSlackDetails, slackChannelName, remoChecked, expertChecked],
  );

  const handleManagerPromote = useCallback(async () => {
    if (!manager) {
      return;
    }
    let slackConfig = undefined;
    if (showSlackDetails) {
      slackConfig = {
        slackChannelName,
        createExpertSlack: expertChecked,
        createRemoSlack: remoChecked,
      };
    }
    setIsUploading(true);
    const managerId = manager.value;
    try {
      const result = await promoteToTopLevelManager(managerId, podType, slackConfig);
      queryClient.invalidateQueries(['fetchWorkerManagers']);
      if (result.success) {
        history.push(buildRoute(Routes.ExpertManagement, { user: managerId }));
      } else {
        defaultApiFetchErrorHandler({ name: 'error', message: result.error, logError: () => null });
      }
    } finally {
      setIsUploading(false);
    }
  }, [
    manager,
    showSlackDetails,
    slackChannelName,
    expertChecked,
    remoChecked,
    podType,
    queryClient,
    history,
  ]);

  return (
    <div className="flex flex-col gap-2">
      <Typography.Subtitle2>Promote User to Team Lead Coordinator (TLC)</Typography.Subtitle2>
      <SelectScaler
        placeholder="Select a user to promote to TLC"
        value={manager}
        onChange={setManager}
        filterOption={() => true}
      />
      <SelectMenu
        placeholder="Select a pod type"
        options={filteredPodTypeOptions}
        selectedOptionId={podType}
        setSelected={setPodType}
      />
      <div
        className="flex cursor-pointer w-60"
        onClick={() => setShowSlackDetails(!showSlackDetails)}
      >
        <IconButton className="mr-2">
          {showSlackDetails ? (
            <FontAwesomeIcon size="sm" icon={['far', 'minus-circle']} />
          ) : (
            <FontAwesomeIcon size="sm" icon={['far', 'plus-circle']} />
          )}
        </IconButton>
        <Typography.Subtitle4 className="text-neutral-400">
          Add a Slack channel (optional)
        </Typography.Subtitle4>
      </div>
      {showSlackDetails && (
        <div className="ml-1 pl-3 border-l border-solid border-neutral-200">
          <TextField
            id="slackChannelName"
            className="mb-2"
            placeholder="Slack channel name"
            value={slackChannelName}
            onInput={event => setSlackChannelName(event?.currentTarget.value)}
          />
          <div className="flex gap-3 mb-2">
            <Typography.Paragraph2>Slack Community:</Typography.Paragraph2>
            <Checkbox
              label="Remo"
              checked={remoChecked}
              onChange={() => setRemoChecked(!remoChecked)}
            />
            <Checkbox
              label="Expert"
              checked={expertChecked}
              onChange={() => setExpertChecked(!expertChecked)}
            />
          </div>
        </div>
      )}
      <div className="mb-2">
        <Button disabled={!canUpload} onClick={handleManagerPromote}>
          Promote User
        </Button>
      </div>
    </div>
  );
}

export const CommunityManagementTab = () => {
  return (
    <div className="flex flex-col gap-4" style={{ maxWidth: 800 }}>
      <PromoteManager />
      <ManuallyAssignWorkers />
      <ManuallyRemoveWorkers />
    </div>
  );
};
